import React, {useEffect} from "react";
import {Box, Button, Form, FormGroup, Rating, Stack, SubmitButton} from "@unibuddy/patron";
import * as yup from "yup";
import {FormikHelpers} from "formik";
import {useAnalytics} from "@unibuddy/tracking";

import {events} from "../../../../../../analytics/productSpecification";
import {useUniversityContext} from "../../../../../../providers/UniversityProvider";

export const CONVERSATION_RATING_POPUP = "Conversation Rating Collection Popup";

type ConversationFeedbackRatingProps = {
    handleSubmit: (rating: number) => Promise<void>;
    handleDismiss: () => void;
};

type FormValues = {
    rating: number;
};

export const ConversationFeedbackRatingForm = ({
    handleSubmit,
    handleDismiss,
}: ConversationFeedbackRatingProps) => {
    const {trackEvent} = useAnalytics();
    const {universitySlug} = useUniversityContext();

    useEffect(() => {
        trackEvent(events.conversationRatingCollectionVisible.name, {
            product: "Assistant",
            universitySlug,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Form
            initialValues={{rating: undefined}}
            validationSchema={{
                rating: yup.string().required("Please select a rating"),
            }}
            onSubmit={async (
                values: {rating: number},
                {setSubmitting}: FormikHelpers<FormValues>,
            ) => {
                setSubmitting(true);
                trackEvent(events.conversationRatingSubmitButtonClicked.name, {
                    product: "Assistant",
                    conversationRating: values.rating,
                    cta: "Submit",
                    where: CONVERSATION_RATING_POPUP,
                    universitySlug,
                });
                try {
                    await handleSubmit(values.rating);
                } catch (error) {
                    console.error(error);
                    setSubmitting(false);
                }
            }}
        >
            {(props: {values: FormValues; isSubmitting: boolean}) => {
                const {values, isSubmitting} = props;
                return (
                    <Stack space="small-medium">
                        <FormGroup label="How helpful was this conversation?">
                            <Rating
                                name="rating"
                                min={1}
                                max={5}
                                minText="Not very helpful"
                                maxText="Extremely helpful"
                            />
                        </FormGroup>
                        <Box gap="small" display="flex">
                            <Button
                                color="default"
                                ghost
                                block
                                size="sm"
                                onClick={() => {
                                    trackEvent(
                                        events.conversationRatingContinueChattingButtonClicked.name,
                                        {
                                            product: "Assistant",
                                            cta: "Continue chatting",
                                            where: CONVERSATION_RATING_POPUP,
                                            universitySlug,
                                        },
                                    );
                                    handleDismiss();
                                }}
                                disabled={isSubmitting}
                            >
                                Continue chatting
                            </Button>
                            <SubmitButton
                                color="primary"
                                block
                                size="sm"
                                disabled={values.rating === undefined}
                            >
                                Submit
                            </SubmitButton>
                        </Box>
                    </Stack>
                );
            }}
        </Form>
    );
};
